import { createAsyncThunk } from "@reduxjs/toolkit";
import { AppId, AppThunkAPIType } from "../../../app/appTypes";
import { deleteWithAuth, getWithAuth, postWithAuth, putWithAuth } from "../../../http";

export type DocumentQuestionType = "YesNo" | "OpenText"
export type DocumentAnswerType = "NONE" | "YES" | "NO" | "NA";
export type DocumentAnalyzedObjectType = "COMMON";

export interface DocumentQuestion {
    id: AppId,
    documentId: number,
    sectionId: string,
    text: string,
    type: DocumentQuestionType,
    order: number,
    globalAnswer: string,
    guid: string
}

export interface DocumentAnalyzedObject {
    id: number;
    documentId: number;
    sectionId: string;
    analyzedObjectId: number;
    order: number;
    objectType: DocumentAnalyzedObjectType;
    name: string;
    description: string;
    result: DocumentAnswerType;
    resultComment: string;
    parentId: number | null;
    children: DocumentAnalyzedObject[]
    answers: DocumentQuestionAnswer[]
    guid: string
}

export interface DocumentQuestionAnswer {
    id: number;
    questionId: number;
    value: DocumentAnswerType;
    analyzedObjectId?: number;
}

export interface CreateUpdateDocumentQuestionPayload {
    text: string,
    type: DocumentQuestionType,
    globalAnswer: string
}

export interface DocumentQuestionAnswerPayload {
    id: number | null,
    questionId: number,
    value: DocumentAnswerType
}

export interface CreateUpdateDocumentAnalyzedObjectPayload {
    sectionId: string,
    name: string,
    description: string,
    objectType: string,
    result: DocumentAnswerType,
    resultComment: string
    parentId: number | null,
    answers: DocumentQuestionAnswerPayload[]
}

export const loadDocumentSectionQuestions = createAsyncThunk<DocumentQuestion[], { documentId: AppId; sectionId: string }
    , AppThunkAPIType>("document-questions/loadDocumentSectionQuestions",
        async ({ documentId, sectionId }, { dispatch, rejectWithValue }) => {
            const result = await dispatch(getWithAuth({
                url: `api/document-questions/${documentId}/sections/${sectionId}/questions`,
            }))
            const { payload } = result
            if (getWithAuth.fulfilled.match(result)) {
                return payload as DocumentQuestion[]
            } else {
                return rejectWithValue(payload ?? { kind: 'unknown' })
            }
        })

export const addDocumentSectionQuestion = createAsyncThunk<DocumentQuestion, { documentId: AppId, sectionId: string, payload: CreateUpdateDocumentQuestionPayload }
    , AppThunkAPIType>("document-questions/addDocumentSectionQuestion", async (data, { dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({
            url: `api/document-questions/${data.documentId}/sections/${data.sectionId}/questions`,
            payload: {
                ...data.payload
            },
        }))
        const { payload } = result
        if (postWithAuth.fulfilled.match(result)) {
            return payload as DocumentQuestion
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })


export const updateDocumentSectionQuestion = createAsyncThunk<DocumentQuestion, { documentId: AppId, sectionId: string, questionId: AppId, payload: CreateUpdateDocumentQuestionPayload }
    , AppThunkAPIType>("document-questions/update-document-section-question", async (data, { dispatch, rejectWithValue }) => {
        const result = await dispatch(putWithAuth({
            url: `api/document-questions/${data.documentId}/sections/${data.sectionId}/questions/${data.questionId}`,
            payload: {
                ...data.payload
            },
        }))
        const { payload } = result
        if (putWithAuth.fulfilled.match(result)) {
            return payload as DocumentQuestion
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })



export const deleteDocumentSectionQuestion = createAsyncThunk<AppId, { documentId: AppId, sectionId: string, questionId: AppId }, AppThunkAPIType>("document-questions/deleteDocumentSectionQuestion",
    async (data, { dispatch, rejectWithValue }) => {

        const result = await dispatch(deleteWithAuth({
            url: `api/document-questions/${data.documentId}/sections/${data.sectionId}/questions/${data.questionId}`,
        }))
        const { payload } = result

        if (deleteWithAuth.fulfilled.match(result)) {
            return data.questionId

        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })


export const loadAnalyzedObjectsForSection = createAsyncThunk<DocumentAnalyzedObject[], { documentId: AppId; sectionId: string }
    , AppThunkAPIType>("document-analyzedobjects/loadAnalyzedObjectsForSection",
        async ({ documentId, sectionId }, { dispatch, rejectWithValue }) => {
            const result = await dispatch(getWithAuth({
                url: `api/document-analyzed-object/${documentId}/sections/${sectionId}`,
            }))
            const { payload } = result
            if (getWithAuth.fulfilled.match(result)) {
                return payload as DocumentAnalyzedObject[]
            } else {
                return rejectWithValue(payload ?? { kind: 'unknown' })
            }
        })



export interface LoadModulesForACMPayload {
    overwriteExisting: boolean;
}

export const loadModulesForACM = createAsyncThunk<DocumentAnalyzedObject[], { documentId: AppId; sectionId: string, params: LoadModulesForACMPayload }
    , AppThunkAPIType>("document-analyzedobjects/loadModulesForACM",
        async ({ documentId, sectionId, params }, { dispatch, rejectWithValue }) => {
            const result = await dispatch(postWithAuth({
                url: `api/document-analyzed-object/${documentId}/sections/${sectionId}/load-modules-for-acm`,
                payload: {
                    ...params
                }
            }))
            const { payload } = result
            if (postWithAuth.fulfilled.match(result)) {
                return payload as DocumentAnalyzedObject[]
            } else {
                return rejectWithValue(payload ?? { kind: 'unknown' })
            }
        })


export interface LoadProcessesForACPPayload {
    overwriteExisting: boolean;
    onlyProcessesFromCriticalModules: boolean;
}

export const loadProcessesForACP = createAsyncThunk<DocumentAnalyzedObject[], { documentId: AppId; sectionId: string, params: LoadProcessesForACPPayload }
    , AppThunkAPIType>("document-analyzedobjects/loadProcessesForACP",
        async ({ documentId, sectionId, params }, { dispatch, rejectWithValue }) => {
            const result = await dispatch(postWithAuth({
                url: `api/document-analyzed-object/${documentId}/sections/${sectionId}/load-processes-for-acp`,
                payload: {
                    ...params
                }
            }))
            const { payload } = result
            if (postWithAuth.fulfilled.match(result)) {
                return payload as DocumentAnalyzedObject[]
            } else {
                return rejectWithValue(payload ?? { kind: 'unknown' })
            }
        })


export interface LoadFunctionalitiesForACFPayload {
    overwriteExisting: boolean;
    onlyFunctionalitiesFromCriticalModules: boolean;
}

export const loadFunctionalitiesForACF = createAsyncThunk<DocumentAnalyzedObject[], { documentId: AppId; sectionId: string, params: LoadFunctionalitiesForACFPayload }
    , AppThunkAPIType>("document-analyzedobjects/loadFunctionalitiesForACF",
        async ({ documentId, sectionId, params }, { dispatch, rejectWithValue }) => {
            const result = await dispatch(postWithAuth({
                url: `api/document-analyzed-object/${documentId}/sections/${sectionId}/load-functionalities-for-acf`,
                payload: {
                    ...params
                }
            }))
            const { payload } = result
            if (postWithAuth.fulfilled.match(result)) {
                return payload as DocumentAnalyzedObject[]
            } else {
                return rejectWithValue(payload ?? { kind: 'unknown' })
            }
        })


export const addDocumentAnalyzedObject = createAsyncThunk<DocumentAnalyzedObject, { documentId: AppId, sectionId: string, payload: CreateUpdateDocumentAnalyzedObjectPayload }
    , AppThunkAPIType>("document-analyzed-object/addDocumentSectionQuestion", async (data, { dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({
            url: `api/document-analyzed-object/${data.documentId}/sections/${data.sectionId}`,
            payload: {
                ...data.payload
            },
        }))
        const { payload } = result
        if (postWithAuth.fulfilled.match(result)) {
            return payload as DocumentAnalyzedObject
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })


export const updateDocumentAnalyzedObject = createAsyncThunk<DocumentAnalyzedObject, { documentId: AppId, sectionId: string, objectId: number, payload: CreateUpdateDocumentAnalyzedObjectPayload }
    , AppThunkAPIType>("document-analyzed-object/updateDocumentAnalyzedObject", async (data, { dispatch, rejectWithValue }) => {
        const result = await dispatch(putWithAuth({
            url: `api/document-analyzed-object/${data.documentId}/sections/${data.sectionId}/${data.objectId}`,
            payload: {
                ...data.payload
            },
        }))
        const { payload } = result
        if (putWithAuth.fulfilled.match(result)) {
            return payload as DocumentAnalyzedObject
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })


export const deleteDocumentAnalyzedObject = createAsyncThunk<number, { documentId: AppId, sectionId: string, objectId: number }, AppThunkAPIType>("document-analyzed-object/deleteDocumentAnalyzedObject",
    async (data, { dispatch, rejectWithValue }) => {

        const result = await dispatch(deleteWithAuth({
            url: `api/document-analyzed-object/${data.documentId}/sections/${data.sectionId}/${data.objectId}`,
        }))
        const { payload } = result

        if (deleteWithAuth.fulfilled.match(result)) {
            return data.objectId

        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })
